import React from 'react';
import { motion } from 'framer-motion';
import { ASSETS } from '../config/assets';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const AggregateLevelsPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  const aggregateLevels = [
    {
      id: 'salt-pepper',
      label: 'Salt & Pepper',
      title: 'Class A - Salt & Pepper',
      description: 'Minimal aggregate exposure. Surface is ground just enough to remove the cream layer, revealing small specks of fine aggregate that resemble salt and pepper.',
      grit: '120-400 grit',
      ideal: 'Modern, minimalist interiors where a subtle, refined appearance is desired.'
    },
    {
      id: 'medium',
      label: 'Medium Aggregate',
      title: 'Class B - Medium Aggregate',
      description: 'Moderate aggregate exposure showing larger stone fragments. The concrete is ground deeper to expose medium-sized aggregates, creating more visual texture and interest.',
      grit: '80-200 grit initial, up to 1500 final',
      ideal: 'Commercial spaces, retail stores, and residential areas wanting a balance of aesthetics and durability.'
    },
    {
      id: 'full',
      label: 'Full Aggregate',
      title: 'Class C - Full Aggregate',
      description: 'Maximum aggregate exposure revealing large stones and full decorative aggregate. Creates a stunning, terrazzo-like appearance with maximum visual impact.',
      grit: '30-100 grit initial, up to 3000 final polish',
      ideal: 'High-end commercial lobbies, luxury homes, and statement floor areas.'
    }
  ];

  const grindingStages = [
    {
      stage: 'Initial Cut (30-100 Grit)',
      description: 'Heavy grinding to expose aggregate and level the surface. Removes coatings and imperfections.'
    },
    {
      stage: 'Refinement (200-400 Grit)',
      description: 'Medium grinding to smooth the surface and further expose aggregate. Begins to create a satin finish.'
    },
    {
      stage: 'Pre-Polish (800-1500 Grit)',
      description: 'Fine grinding removes scratches from previous steps. Surface begins to develop reflectivity.'
    },
    {
      stage: 'Final Polish (3000+ Grit)',
      description: 'Ultra-fine polishing creates the high-gloss, mirror-like finish. Maximum light reflection and clarity.'
    }
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="aggregate-levels-page">
      {/* Hero */}
      <section className="section-spacing bg-[radial-gradient(1200px_600px_at_50%_50%,#EEEDEC_0%,#F7F7F6_45%,#FAFAF9_100%)]">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Levels of Aggregate Exposure</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Understanding the different classes of polished concrete finishes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Visual Reference */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <motion.div {...fadeInUp}>
            <Card className="overflow-hidden mb-12">
              <img
                src={ASSETS.images.aggregateLevels}
                alt="Aggregate Levels Comparison"
                className="w-full h-auto"
                data-testid="aggregate-levels-image"
              />
            </Card>
          </motion.div>

          {/* Educational Diagrams */}
          <motion.div className="grid md:grid-cols-2 gap-6 mb-12" {...fadeInUp}>
            <Card className="overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/a49rxzah_levels%20of%20aggregate.jpeg"
                alt="Aggregate Exposure Levels Diagram"
                className="w-full h-auto"
                data-testid="aggregate-diagram-1"
              />
            </Card>
            <Card className="overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/575kyxcy_images-17.jpeg"
                alt="Concrete Finish Types Diagram"
                className="w-full h-auto"
                data-testid="aggregate-diagram-2"
              />
            </Card>
          </motion.div>

          <motion.div {...fadeInUp}>
            <Tabs defaultValue="salt-pepper" className="w-full" data-testid="aggregate-tabs">
              <TabsList className="grid w-full grid-cols-3 mb-8">
                {aggregateLevels.map((level) => (
                  <TabsTrigger key={level.id} value={level.id} data-testid={`tab-${level.id}`}>
                    {level.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {aggregateLevels.map((level) => (
                <TabsContent key={level.id} value={level.id} data-testid={`tab-content-${level.id}`}>
                  <Card>
                    <CardContent className="pt-6">
                      <h3 className="text-2xl font-bold mb-4">{level.title}</h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {level.description}
                      </p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold mb-2">Grinding Range</h4>
                          <p className="text-sm text-muted-foreground">{level.grit}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Ideal For</h4>
                          <p className="text-sm text-muted-foreground">{level.ideal}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </motion.div>

          {/* Additional Aggregate Comparison */}
          <motion.div {...fadeInUp} className="mt-16 mb-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">
                Blue Metal vs Riverstone
                <br />
                Full Aggregate Exposure Comparison
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Side by side comparison showing the different colours of full aggregate exposure in Polished Concrete finishes real time.
              </p>
            </div>
            <Card className="overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/g5i4ftds_IMG-20250423-WA0037.jpg"
                alt="Detailed Aggregate Levels Comparison - Salt & Pepper vs Medium vs Full Exposure"
                className="w-full h-auto"
                data-testid="detailed-aggregate-comparison"
              />
            </Card>
            <div className="text-center mt-4">
              <p className="text-sm text-muted-foreground">
                Left: new concrete pour vs Right: old concrete pour
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Grinding Process */}
      <section className="section-spacing bg-secondary/20">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl font-bold mb-4">The Grinding & Polishing Process</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Understanding the grit levels and stages of concrete polishing
            </p>
          </motion.div>

          <motion.div className="max-w-3xl mx-auto" {...fadeInUp}>
            <Accordion type="single" collapsible className="w-full" data-testid="grinding-accordion">
              {grindingStages.map((stage, index) => (
                <AccordionItem key={index} value={`item-${index}`} data-testid={`accordion-item-${index}`}>
                  <AccordionTrigger className="text-left" data-testid={`accordion-trigger-${index}`}>
                    <span className="font-semibold">{stage.stage}</span>
                  </AccordionTrigger>
                  <AccordionContent data-testid={`accordion-content-${index}`}>
                    <p className="text-muted-foreground">{stage.description}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div {...fadeInUp}>
              <Card className="h-full">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Durability</h3>
                  <p className="text-sm text-muted-foreground">
                    All levels of polished concrete are extremely durable, with higher grit polishing providing 
                    increased resistance to wear and easier maintenance.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.1 }}>
              <Card className="h-full">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Reflectivity</h3>
                  <p className="text-sm text-muted-foreground">
                    Light reflectivity increases with higher grit levels. 3000 grit produces a mirror-like finish, 
                    while 400 grit creates a satin appearance.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
              <Card className="h-full">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-3">Maintenance</h3>
                  <p className="text-sm text-muted-foreground">
                    Polished concrete requires minimal maintenance - regular dust mopping and occasional damp cleaning. 
                    Sealer reapplication every 5-10 years.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AggregateLevelsPage;
